import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CalendarClock, Check, X, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { Pill } from "./util";

const fmt = (d) => (d ? d.slice(0, 10).split("-").reverse().join("/") : "SIN FECHA");
const rango = (a, b) => (b && b !== a ? `${fmt(a)} – ${fmt(b)}` : fmt(a));
const TONO = { pendiente: "amber", confirmado: "green", rechazado: "red" };

const Item = ({ x, onChanged }) => {
  const [f, setF] = useState(x.fecha_propuesta);
  const tid = x.numero;
  const post = async (acc, body, msg) => {
    try { await api.post(`/mi-dinero/reprogramaciones/${x.id}/${acc}`, body); toast.success(msg); onChanged(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const ult = x.historial?.[x.historial.length - 1];
  return (
    <div className="rounded-xl border p-2 grid gap-2 md:grid-cols-[1.3fr_1fr_1fr_1.5fr_auto] md:items-center text-sm" data-testid={`reprog-${tid}`}>
      <div className="flex flex-wrap items-center gap-1.5"><b className="text-xs uppercase">{x.cliente}</b>
        <Link to={`/pedidos/${x.id}`} className="rounded bg-violet-700 text-white text-[10px] font-bold px-1.5 py-0.5" data-testid={`reprog-link-${tid}`}>{x.numero}</Link></div>
      <div className="text-xs">Fecha anterior<br /><b>{rango(x.fecha_anterior, x.fecha_anterior_hasta)}</b></div>
      <div className="text-xs">Nueva fecha propuesta<br />
        {x.estado === "pendiente" ? <Input type="date" value={f} onChange={(e) => setF(e.target.value)} className="h-8 mt-0.5" data-testid={`reprog-fecha-${tid}`} />
          : <b>{fmt(x.fecha_confirmada || x.fecha_propuesta)}</b>}</div>
      <div className="text-xs">Motivo: <b>{x.motivo}</b><div className="text-[10px] text-zinc-500">Reportó {x.por_nombre || x.por} · {new Date(x.at).toLocaleString("es-GT", { timeZone: "America/Guatemala" })}</div>
        {ult && x.estado !== "pendiente" && <div className="text-[10px] text-zinc-500" data-testid={`reprog-hist-${tid}`}>{ult.accion.replace("_", " ")}: {ult.nombre || ult.por} · {new Date(ult.at).toLocaleString("es-GT", { timeZone: "America/Guatemala" })}</div>}</div>
      <div className="flex flex-wrap gap-1 items-center">
        {x.estado === "pendiente" ? <>
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-xs" onClick={() => post("confirmar", { fecha: f }, "Reprogramación confirmada: Hoja de Pedidos actualizada")} data-testid={`reprog-confirmar-${tid}`}><Check className="w-4 h-4 mr-1" />CONFIRMAR REPROGRAMACIÓN</Button>
          <Button size="sm" variant="outline" className="text-xs text-red-700 border-red-300" onClick={() => { const m = window.prompt("Motivo del rechazo (opcional):"); if (m !== null) post("rechazar", { motivo: m }, "Reprogramación rechazada"); }} data-testid={`reprog-rechazar-${tid}`}><X className="w-4 h-4 mr-1" />RECHAZAR</Button>
        </> : <>
          <Pill tone={TONO[x.estado]} testid={`reprog-estado-${tid}`}>{x.estado.toUpperCase()}</Pill>
          <button className="text-[11px] underline text-zinc-600" onClick={() => { const m = window.prompt("Motivo para deshacer:"); if (m) post("deshacer", { motivo: m }, "Deshecho"); }} data-testid={`reprog-deshacer-${tid}`}><Undo2 className="inline w-3 h-3" /> Deshacer</button>
        </>}
      </div>
    </div>
  );
};

export const Reprogramaciones = () => {
  const [d, setD] = useState(null);
  const load = useCallback(() => { api.get("/mi-dinero/reprogramaciones").then(({ data }) => setD(data)).catch((e) => toast.error(formatApiError(e))); }, []);
  useEffect(() => { load(); }, [load]);
  if (!d || !d.items.length) return null;
  return (
    <div className="rounded-xl border bg-white p-3 space-y-2" data-testid="reprogramaciones">
      <div className="flex items-center gap-2 font-black"><CalendarClock className="w-5 h-5 text-teal-600" />Reprogramaciones del repartidor
        {d.pendientes > 0 && <Pill tone="amber" testid="reprog-pendientes">{d.pendientes} por confirmar</Pill>}</div>
      {d.items.map((x) => <Item key={`${x.id}-${x.estado}-${x.at}`} x={x} onChanged={load} />)}
    </div>
  );
};
